import { Link } from 'react-router-dom';

import { PictoCall, PictoCallCenter } from '@/assets/icons-april-fr/pictos';
import AprilPicto from '@/components/shared/april-picto';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { HOST_MB } from '@/lib/constants';
import { useContractsData } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { getApporteur1LastContract } from '@/lib/contracts';

const formatPhone = (phone?: string) => {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

export interface ContactProps {
  /** Tous les contrats sont apportés par APRIL Moto (43397). */
  hasApporteur43397?: boolean;
}

const Contact = ({ hasApporteur43397 }: ContactProps) => {
  const apporteur = getApporteur1LastContract(useContractsData());
  const whitelabelUser = useCurrentUser().whitelabel;
  const isAmGestion = HOST_MB !== 43397;

  // Sur l'espace APRIL Moto, on masque le bloc contact pour les clients sans contrat 43397 dont le
  // whitelabel est l'id 1. Sur AM Gestion, le bloc du courtier est affiché pour tout le monde.
  if (!isAmGestion && !hasApporteur43397 && Number(whitelabelUser) === 1) return null;

  if (isAmGestion && apporteur) {
    return (
      <Card data-tutorial="home-contact" className="relative overflow-hidden bg-primary text-primary-foreground mt-6">
        <div className="absolute -top-20 -right-10 w-56 h-56 bg-white/10 rounded-full" />
        <div className="absolute -bottom-24 -left-12 w-48 h-48 bg-white/5 rounded-full" />

        <CardHeader className="relative z-10">
          <CardTitle className="text-lg text-primary-foreground">Une question ?</CardTitle>
        </CardHeader>
        <CardContent className="relative z-10 space-y-4">
          <p className="text-sm text-primary-foreground">
            Besoin d'un renseignement ? Nos équipes sont là pour vous aider.
          </p>

          {apporteur.phone && (
            <div className="flex items-start gap-3">
              <AprilPicto icon={PictoCall} size="md" inverted className="mt-0.5 shrink-0" />
              <p className="text-sm text-primary-foreground">
                Contactez-nous par téléphone au <a href={`tel:${apporteur.phone}`} className="text-primary-foreground font-semibold hover:underline">{formatPhone(apporteur.phone)}</a>.
              </p>
            </div>
          )}

          {apporteur.email_principal && (
            <div className="flex items-start gap-3">
              <AprilPicto icon={PictoCallCenter} size="md" inverted className="mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-primary-foreground mb-1">Vous préférez nous contacter par mail ? Envoyez-nous votre demande à l'adresse suivante :</p>
                <a
                  href={`mailto:${apporteur.email_principal}`}
                  className="text-primary-foreground font-medium hover:underline"
                >
                  {apporteur.email_principal}
                </a>
              </div>
            </div>
          )}

          <Button asChild variant="outline" className="w-full mt-6 !border-white !text-white hover:!bg-white/20 hover:!border-white cursor-pointer">
            <Link to="/contact">Contactez-nous</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-tutorial="home-contact" className="relative overflow-hidden bg-primary text-primary-foreground mt-6">
      {/* Decorative bubbles */}
      <div className="absolute -top-20 -right-10 w-56 h-56 bg-white/10 rounded-full" />
      <div className="absolute -bottom-24 -left-12 w-48 h-48 bg-white/5 rounded-full" />

      <CardHeader className="relative z-10">
        <CardTitle className="text-lg text-primary-foreground">Besoin d'aide ?</CardTitle>
      </CardHeader>
      <CardContent className="relative z-10 space-y-4">
        {hasApporteur43397 ? (
          <>
            <p className="text-sm text-primary-foreground">
              Une question ? Besoin d'un renseignement ? Nos équipes sont là pour vous aider.
            </p>

            <div className="flex items-start gap-3">
              <AprilPicto icon={PictoCall} size="md" inverted className="mt-0.5 shrink-0" />
              <p className="text-sm text-primary-foreground">
                Contactez nous par téléphone au <strong className="text-primary-foreground">02 47 51 07 07 puis tapez "1"</strong>,
                du lundi au vendredi, de 9h00 à 18h00.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <AprilPicto icon={PictoCallCenter} size="md" inverted className="mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-primary-foreground mb-1">Vous préférez nous contacter par mail ?</p>
                <a
                  href="mailto:moncontrat@april-moto.com"
                  className="text-primary-foreground font-medium hover:underline"
                >
                  moncontrat@april-moto.com
                </a>
              </div>
            </div>

            <Button asChild variant="outline" className="w-full mt-6 !border-white !text-white hover:!bg-white/20 hover:!border-white cursor-pointer">
              <Link to="/contact">Contactez-nous</Link>
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <AprilPicto icon={PictoCall} size="md" inverted className="mt-0.5 shrink-0" />
              <p className="text-sm text-primary-foreground">
                Besoin d'un renseignement ? Nos équipes sont là pour vous aider. Contactez-nous par téléphone.
              </p>
            </div>

            <Button asChild className="w-full mt-6 !bg-white !text-primary hover:!bg-white/90 cursor-pointer">
              <Link to="/contact">Contactez-nous</Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Contact;
